import videojs from "video.js";
import { MOBILE_OS, TOKEN_TYPES } from "@/configs/SDK";
import { VIDEO_PLAYER_CONFIGS } from "@/configs/Settings";
import HelperMixins from "@/mixins/HelperMixins";
import "videojs-contrib-quality-levels";
import _debounce from "lodash/debounce";
import ConvivaService from "@/ultis/ConvivaService";

export default {
   name: "VideoJsPlayer",
   mixins: [HelperMixins],
   props: {
      useConvivaTracking: {
         type: Boolean,
         default: false
      },
      convivaMetadata: {
         type: Object,
         default: function() {
            return {};
         }
      },
      urlSources: {
         type: Array,
         default: function() {
            return [];
         }
      },
      coverImage: {
         type: String,
         default: function() {
            return "";
         }
      },
      streamInfo: {
         type: Object,
         default: function() {
            return {};
         }
      },
      playerWrapper: {
         type: String,
         default: "my-video"
      },
      options: {
         type: Object,
         default: () => ({ ...defaultOptions() })
      },
      token: {
         type: String,
         default: ""
      },
      typeToken: {
         type: String,
         default: TOKEN_TYPES.AKAIMA // akaima
      },
      baseUrl: {
         type: String,
         default: ""
      },
      forceAutoPlayWithSound: {
         type: Boolean,
         default: false
      },
      isRecordVideo: {
         type: Boolean,
         default: false
      }
   },
   data() {
      return {
         convivaServiceLoaded: false,
         convivaService: null,
         reportPlaybackEnded: false,
         reportPlaybackRequested: false,
         player: null,
         qualityLevels: null,
         currentVideoQuality: null,
         listQuality: [{ height: "auto", selected: true }],

         retryPlayVideoNumber: 0,
         maximumRetryPlay: 3,
         showWaitingTime: 0,
         urlPlaying: null,
         indexUrlPlaying: 0,

         timeWaitingForSwitchUrl: VIDEO_PLAYER_CONFIGS.TIME_WAITING_ERROR,
         waitingForSwitchUrlJobId: null,
         isMuted: null,
         isShowMuteBtn: true,
         isPaused: null,
         isShowResumeVideo: false,
         hasErrorPlaying: false,
         mobileOS: MOBILE_OS,
         isLive: false,
         hidden: "",
         visibilityChange: "",
      };
   },
   watch: {
      async isFullscreen(val) {
         if (!val && this.player) {
            if (this.viewerUsingMobileOS === this.mobileOS.iOS) {
               await this.pauseVideo(false);
               if (!this.hasErrorPlaying)
                  this.isShowResumeVideo = true;
            } else {
               await this.resumeVideo(false);
               this.isShowResumeVideo = false;
            }
         }
      },
      urlPlaying(val) {
         if (val) {
            this.$emit("updateUrlPlaying", val);
         }
      },
      async urlSources(val) {
         if (val.length) {
            this.indexUrlPlaying = 0;
            await this.parseUrlPlaying();
            this.reloadUrlSource();
         }
      },
      isShowResumeVideo(val) {
         this.$emit("updateStateShowResumeVideo", val);
      }
   },
   computed: {
      imgCover: function() {
         return this.coverImage ? this.coverImage : "";
      },
      typeVideo: function() {
         if (!this.urlPlaying) return "";

         let extension = this.urlPlaying.split(".").pop();
         if (extension === "mp4") {
            return "video/mp4";
         } else if (extension === "m3u8") {
            return "application/x-mpegURL";
         } else if (extension === "webm") {
            return "video/webm";
         }
      },
      isFullscreen() {
         if (this.player) {
            return this.player.isFullscreen();
         }
         return false;
      },
      viewerUsingMobileOS() {
         return this.getMobileOperatingSystem();
      }
   },
   created() {
      // Set the name of the hidden property and the change event for visibility
      if (typeof document.hidden !== "undefined") { // Opera 12.10 and Firefox 18 and later support
         this.hidden = "hidden";
         this.visibilityChange = "visibilitychange";
      } else if (typeof document.msHidden !== "undefined") {
         this.hidden = "msHidden";
         this.visibilityChange = "msvisibilitychange";
      } else if (typeof document.webkitHidden !== "undefined") {
         this.hidden = "webkitHidden";
         this.visibilityChange = "webkitvisibilitychange";
      }
   },
   async mounted() {
      // Warn if the browser doesn't support addEventListener or the Page Visibility API
      if (typeof document.addEventListener === "undefined" || this.hidden === undefined) {
         console.log("This demo requires a browser, such as Google Chrome or Firefox, that supports the Page Visibility API.");
      } else {
         // Handle page visibility change
         document.addEventListener(this.visibilityChange, this.handleVisibilityChange, false);
      }

      await this.parseUrlPlaying();
      if (this.useConvivaTracking) {
         this.convivaService = new ConvivaService();
         this.convivaService.loadScript(async () => {
            this.convivaServiceLoaded = true;
            await this.initialPlayer();
            // Handle event
            this.handleVideoEvents();
         });
      } else {
         await this.initialPlayer();
         // Handle event
         this.handleVideoEvents();
      }
   },
   methods: {
      // If the page is hidden, pause the video;
      // if the page is shown, play the video
      async handleVisibilityChange() {
         if (document[this.hidden]) {
            // await this.player.pause();
         } else {
            this.resumeVideo();
         }
      },

      async getSeekableDuration() {
         let seekable = this.player.seekable();
         return seekable && seekable.length ? seekable.end(0) - seekable.start(0) : 0;
      },

      onSeekPercentChange(seekPercent, duration) {
         var seekable = this.player.seekable();
         if(seekable && seekable.length) {
            // constrain currentTime
            this.player.currentTime(Math.max(0, Math.min(seekable.end(0), seekable.start(0) + (seekPercent * duration))));
         }
      },

      /**
       *
       * @param checkShowResume
       * @returns {Promise<void>}
       */
      async resumeVideo(checkShowResume = true) {
         if (this.player) {
            await this.player.play().then(() => {
               this.player.muted(false);
               this.player.controls(this.options?.controls !== undefined ? this.options?.controls : defaultOptions().controls);

               this.isMuted = this.player.muted();
               this.isPaused = this.player.paused();
               this.isShowMuteBtn = true;

               if (checkShowResume) {
                  this.checkShowResumeByPureJS();
               }
            });
         }
      },
      /**
       *
       * @param checkShowResume
       * @returns {Promise<void>}
       */
      async pauseVideo(checkShowResume = true) {
         if (this.player) {
            await this.player.pause();
            this.player.controls(this.options?.controls !== undefined ? this.options?.controls : defaultOptions().controls);
            this.isPaused = this.player.paused();
            if (checkShowResume) {
               this.checkShowResumeByPureJS();
            }
         }
      },
      checkShowResumeByPureJS() {
         let myVideo = document.getElementById(this.playerWrapper).querySelector("video");
         if (myVideo.paused) {
            this.isShowResumeVideo = true;
         } else {
            this.isShowResumeVideo = false;
         }
      },
      unMuteVideo() {
         if (this.player) {
            this.player.play().then(() => {
               this.player.muted(false);
               this.isMuted = this.player.muted();
            });
         }
      },
      async parseUrlPlaying() {
         if (this.urlSources.length) {
            this.urlPlaying = `${this.baseUrl || ""}${this.urlSources[this.indexUrlPlaying]}`;

            // Check encryption:
            if (this.token && this.typeToken === TOKEN_TYPES.JWT) {
               await this.parseUrlWithToken();
            } else if (this.token && this.typeToken === TOKEN_TYPES.AKAIMA) {
               this.urlPlaying = `${this.urlPlaying}?hdnea=${this.token}`;
            }
         }
      },
      debounceChangeVideoQuality: _debounce(function(height) {
         this.changeVideoQuality(height);
      }, 2000),
      changeVideoQuality(height) {
         if (!this.qualityLevels?.length || !height) return;
         this.currentVideoQuality = height;
         for (var i = 0; i < this.qualityLevels.length; i++) {
            let qualityLevel = this.qualityLevels[i];
            if (height === "auto") {
               qualityLevel.enabled = true;
            } else if (qualityLevel.height == height) {
               qualityLevel.enabled = true;
            } else {
               qualityLevel.enabled = false;
            }
         }
      },
      initQualityLevels() {
         this.qualityLevels = this.player.qualityLevels();

         console.log("qualityLevels", this.player.qualityLevels());

         this.qualityLevels.on("addqualitylevel", (event) => {
            let qualityLevel = event.qualityLevel;
            this.$emit("addQualityLevel", qualityLevel);
         });

         if (this.currentVideoQuality) this.changeVideoQuality(this.currentVideoQuality);

         this.qualityLevels.on("change", () => {
            if (this.qualityLevels.selectedIndex === -1) return;
            const selectedLevel = this.qualityLevels[this.qualityLevels.selectedIndex];
            this.$emit("changeQualityLevel", selectedLevel);
            console.log("Change level:", selectedLevel);
            if (this.currentVideoQuality != selectedLevel.height) {
               this.changeVideoQuality(this.currentVideoQuality);
            }
         });
      },
      async initialPlayer() {
         try {
            const videoJsOptions = { ...defaultOptions(), ...this.options,loop:false };
            this.player = videojs(
               this.$refs.videoPlayer,
               //turn off autoplay when use Conviva to prevent video render first frame before conviva is initialize
               { ...videoJsOptions, autoplay: this.useConvivaTracking ? false : videoJsOptions.autoplay },
               () => {
                  this.initQualityLevels();
               }
            );
            this.player.src({ type: this.typeVideo, src: this.urlPlaying });

            if (this.useConvivaTracking) {
               this.convivaService?.init(this.player);
            }
            this.playVideo();
         } catch (e) {
            console.log("Error", e);
         }
      },
      async destroyPlayer() {
         if (this.player) {
            await this.player.dispose();
            this.player = null;
         }
         if (this.useConvivaTracking) {
            this.convivaService?.destroy();
            this.convivaService = null;
         }
      },
      addTokenBeforeRequest(options) {
         options.uri = `${options.uri}?t=${this.token}`;
         return options;
      },
      handleVideoEvents() {
         if (!this.player) return;

         let _this = this;

         this.player.on("ready", () => {
            this.player.tech().on("retryplaylist", async () => {
               this.hasErrorPlaying = true;
               this.isShowResumeVideo = false;
               this.createWaitingSwitchUrlJob(false);
            });

            this.player.reloadSourceOnError({
               // getSource allows you to override the source object used when an error occurs
               getSource: function(reload) {
                  console.log("Reloading because of an error");
                  // call reload() with a fresh source object
                  // you can do this step asynchronously if you want (but the error dialog will
                  // show up while you're waiting)
                  reload({
                     src: _this.urlPlaying,
                     type: _this.typeVideo
                  });
               }
               // errorInterval specifies the minimum amount of seconds that must pass before
               // another reload will be attempted
               // errorInterval: 5
            });
         });

         this.player.on("canplay", () => {
            // setTimeout(() => {
            //    if (!this.isPlayedVideo) {
            //       this.isShowResumeVideo = true;
            //    }
            // }, 1000)
            this.$emit("loaded-video", {
               duration: this.player.duration()
            });

            this.isPaused = this.player.paused();
            if (this.isPaused) {
               this.checkShowResumeByPureJS();
            }

            this.isMuted = this.player.muted();
            if (this.isMuted) {
               this.isShowMuteBtn = true;
            }
         });

         this.player.on("play", () => {
            if (this.useConvivaTracking && !this.reportPlaybackRequested) {
               this.convivaService?.reportPlaybackRequested({ ...this.convivaMetadata });
               this.reportPlaybackRequested = true;
               this.reportPlaybackEnded = false;
            }
         });

         this.player.on("playing", () => {
            this.hasErrorPlaying = false;
            this.isShowResumeVideo = false;
            this.clearWaitingSwitchUrlJob();
            this.checkShowResumeByPureJS();
         });

         this.player.on("loadedmetadata", async () => {
            let duration = this.player.duration();
            if (!isFinite(duration)) { // Live
               duration = await this.getSeekableDuration();
               this.isLive = true;
            }
            this.$emit("loadedmetadata", {
               duration: duration
            });
         });

         this.player.on("error", async () => {
            this.hasErrorPlaying = true;
            this.isShowResumeVideo = false;

            if (this.useConvivaTracking) {
               const errMsg = "Error Message: " + this.player.error().message + ", Error Code: " + this.player.error().code;
               this.convivaService?.reportPlaybackError(errMsg);
            }
            this.createWaitingSwitchUrlJob(true);
         });

         this.player.on("fullscreenchange", function() {
            if (_this.player.isFullscreen()) {
               _this.$emit("onEnterFullscreen");
            } else {
               _this.$emit("onExitFullscreen");
            }
         });

         this.player.on("dispose", function() {
            if (this.useConvivaTracking) {
               this.convivaService?.destroy();
               this.convivaService = null;
            }
         });

         this.player.on("ended", function() {
             if(_this.options.loop){
                _this.playbackEnded();
                this.play();
             }
             else if(_this.isLive){
                _this.$root.$emit("host-end-stream");
                this.dispose();
             }
         });

         this.player.on("pause", () => {
            if (!this.hasErrorPlaying)
               this.isShowResumeVideo = true;
         })

         if (this.isRecordVideo) {
            this.checkShowResumeByPureJS();
         }

         this.player.on("timeupdate", async function() {
            this.isShowResumeVideo = false;
            let currentTime = this.currentTime();
            _this.$emit("timeUpdate", {
               currentTime: currentTime
            });
            _this.clearWaitingSwitchUrlJob();

            if (_this.isLive) {
               let  duration = await _this.getSeekableDuration();
               _this.$emit("loadedmetadata", {
                  duration: duration,
                  isLive: _this.isLive
               });
            }
         });
      },
      async playVideo() {
         const allowPlayVideo = this.useConvivaTracking ? this.convivaServiceLoaded : true;
         if (allowPlayVideo) {
            await this.player.play();
            if (this.forceAutoPlayWithSound) {
               this.player.muted(false);
               this.isPaused = this.player.paused();
               if (this.isPaused) {
                  this.player.muted(true);
                  await this.player.play();
                  this.isPaused = this.player.paused();
                  this.isMuted = this.player.muted();
                  if (this.isPaused && !this.hasErrorPlaying) {
                     this.isShowResumeVideo = true;
                  }
                  if (this.isMuted) {
                     this.isShowMuteBtn = true;
                  }
               }
               this.isMuted = this.player.muted();
            }
         }
      },
      updateCurrentTimePlaying(currentTime) {
         this.player.currentTime(parseInt(currentTime));
      },
      parseUrlWithToken() {
         let _this = this;
         videojs.Hls.xhr.beforeRequest = (options) => {
            return _this.addTokenBeforeRequest(options);
         };
      },
      reloadUrlSource() {
         this.player?.src({
            src: this.urlPlaying,
            type: this.typeVideo
         });
      },
      async refreshNewIndexUrl() {
         let newIndex = this.indexUrlPlaying + 1;
         if (this.urlSources.length && newIndex <= this.urlSources.length - 1) {
            this.indexUrlPlaying = newIndex;
         } else {
            this.urlPlaying = null;
            this.$root.$emit(`reload-video-sources`);
            this.indexUrlPlaying = 0;
         }
      },
      clearWaitingSwitchUrlJob() {
         if (!this.waitingForSwitchUrlJobId) {
            clearTimeout(this.waitingForSwitchUrlJobId);
            this.waitingForSwitchUrlJobId = null;
         }
      },
      createWaitingSwitchUrlJob(error = false) {
         if (!this.waitingForSwitchUrlJobId) {
            this.waitingForSwitchUrlJobId = setTimeout(async () => {
               //if error then auto collected otherwise collect manually
               if (!error && this.useConvivaTracking)
                  this.convivaService?.reportPlaybackError("Error message: Unable to retry playlist, switching to another stream url");
               //this.playbackEnded();

               this.waitingForSwitchUrlJobId = null;
               await this.refreshNewIndexUrl();
               await this.parseUrlPlaying();
               this.hasErrorPlaying = false;
               this.isShowResumeVideo = true;
               this.reloadUrlSource();

               //set new url in conviva tracking
               if (this.useConvivaTracking) {
                  const { STREAM_URL } = this.convivaService?.getConvivaInstance().Constants;
                  if (STREAM_URL) this.convivaService?.setContentInfo({ [STREAM_URL]: this.urlPlaying });
               }
               //
            }, this.timeWaitingForSwitchUrl * 1000);
         }
      },
      playbackEnded() {
         if (this.useConvivaTracking && !this.reportPlaybackEnded) {
            this.convivaService?.destroy();
            this.reportPlaybackEnded = true;
            this.reportPlaybackRequested = false;
            this.convivaService.initConviva();
         }
      }
   },
   async beforeDestroy() {
      await this.destroyPlayer();
   }
};

function defaultOptions() {
   return {
      autoplay: true,
      preload: "auto",
      controlBar: {
         playToggle: false,
         captionsButton: false,
         chaptersButton: false,
         subtitlesButton: false,
         remainingTimeDisplay: false,
         progressControl: {
            seekBar: false
         },
         fullscreenToggle: true,
         playbackRateMenuButton: false,
         pictureInPictureToggle: false
      },
      html5: {
         hls: {
            overrideNative: !videojs.browser.IS_SAFARI
         },
         vhs: {
            overrideNative: !videojs.browser.IS_SAFARI
         },
         nativeAudioTracks: false,
         nativeVideoTracks: false,
         nativeTextTracks: false
      },
      loop: true,
      controls: false,
      muted: true
   };
}
